/* eslint-disable max-lines */
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgTemplateOutlet } from '@angular/common';
import type {
  ElementRef,
  QueryList,
  SimpleChanges,
  TemplateRef,
  TrackByFunction,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { deepEqual } from 'fast-equals';
import {
  BehaviorSubject,
  combineLatest as observableCombineLatest,
  Observable,
  Subject,
} from 'rxjs';
import { debounceTime, startWith, take, takeUntil } from 'rxjs/operators';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsMode, AnalyticsViewContext } from '@core/analytics-types';
import { CandidateAnalytics, TrackUnhireTrigger } from '@core/candidate-util';
import { ElementInjectorDirective } from '@core/common-legacy';
import type { InfoboxType } from '@core/components-infobox-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { injectCountryMap } from '@core/country-data';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { AssignedLocationsInfoContainerComponent } from '@core/locations-smart-ui';
import type { Country } from '@core/locations-types';
import type { JobItem } from '@core/offers-types';
import { OfferKind, OfferStatus } from '@core/offers-types';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import type { IconName } from '@core/user-interface-legacy';
import {
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ButtonDropdownIndicatorDirective,
  ButtonLoadingDirective,
  CheckboxComponent,
  ConnectedOverlayDirective,
  DialogBodyComponent,
  DialogData,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  DropdownWidth,
  EmptyStateComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  OverlayOriginDirective,
  RadioComponent,
  RtStatefulComponent,
  RtTooltip,
} from '@core/user-interface-legacy';
import { groupByKey, includesWithTrim, objectKeys } from '@core/utils-legacy';

import type { OfferSelectDialogItem, OfferSelectDialogItemBase } from '../offer-select.types';

export enum OffersAssignmentsSelectModes {
  Single = 'single',
  Multi = 'multi',
  Reorder = 'reorder',
}

type State = {
  groupedOffersByKind: GroupByKind[] | null;
  searchControlValue: string;
  newJobAssignments: number[];
  newTalentPoolAssignments: number[];
  activeSort: string;
  countries: Record<string, Country>;
};

enum TemplateKind {
  Default = 'default',
  Group = 'group',
}

enum SortOptions {
  Department = 'department',
  Location = 'location',
  Status = 'status',
  Title = 'title',
  Country = 'country',
}

type SortOptionsType =
  | SortOptions.Department
  | SortOptions.Country
  | SortOptions.Location
  | SortOptions.Status
  | SortOptions.Title;

type TemplateGroup = {
  groupTitle?: string;
  items: OfferSelectDialogItem[];
  templateKind: TemplateKind.Group | TemplateKind.Default;
  key?: string;
};

type GroupByKind = {
  title: string;
  groups: TemplateGroup[];
  sortable: boolean;
};

type DialogData = {
  assignments: number[];
  offers: OfferSelectDialogItem[];
  heading: string;
  subheading: string;
  submitLabel: string;
  selectMode: OffersAssignmentsSelectModes;
  initialAssignments: number[];
  jobAssignments?: number[];
  talentPoolAssignments?: number[];
  initialJobAssignments?: number[];
  initialTalentPoolAssignments?: number[];
  additionalInfobox?: {
    iconName: IconName;
    type: InfoboxType;
    copyTranslationKey: string;
  };
  offersWithAssignedHires?: JobItem[];
  isAlwaysSaveable?: boolean;
  hasInitialAssignments?: boolean;
  hasGoBack?: boolean;
  isLoading?: boolean;
  saveHandler$?: (offerIds: number[], talentPoolIds: number[]) => Observable<boolean>;
};

@Component({
  selector: 'rt-offers-select-dialog-form',
  templateUrl: './offers-select-dialog-form.component.html',
  styleUrls: ['./offers-select-dialog-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    CandidateAnalytics,
    ...provideAnalyticsViewContext(AnalyticsViewContext.OffersSelectDialog),
  ],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    GroupComponent,
    BoxComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    HeadingComponent,
    ButtonComponent,
    ButtonDropdownIndicatorDirective,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    CdkDropList,
    ElementInjectorDirective,
    NgTemplateOutlet,
    EmptyStateComponent,
    DialogFooterComponent,
    ButtonLoadingDirective,
    CdkDrag,
    DividerComponent,
    RadioComponent,
    CheckboxComponent,
    OfferStatusIndicatorComponent,
    BadgeComponent,
    RtTooltip,
    AssignedLocationsInfoContainerComponent,
    TranslatePipe,
    InfoboxComponent,
  ],
})
export class OffersSelectDialogFormComponent extends RtStatefulComponent<State> {
  private readonly dialog = inject(DialogRef);
  private readonly i18n = inject(I18nService);
  private readonly candidateAnalytics = inject(CandidateAnalytics);
  protected readonly cdRef: ChangeDetectorRef;
  public readonly data = inject<DialogData>(DialogData, { optional: true });

  public readonly heading = this.data?.heading!;
  public readonly subheading = this.data?.subheading!;
  public readonly submitLabel = this.data?.submitLabel ?? this.i18n.translate('app.common.save');
  public readonly jobAssignments = this.data?.jobAssignments || [];
  public readonly talentPoolAssignments = this.data?.talentPoolAssignments || [];
  public readonly initialJobAssignments = this.data?.initialJobAssignments;
  public readonly initialTalentPoolAssignments = this.data?.initialTalentPoolAssignments;
  public readonly selectMode = this.data?.selectMode ?? OffersAssignmentsSelectModes.Multi;
  public readonly isAlwaysSaveable = this.data?.isAlwaysSaveable ?? false;
  public readonly hasInitialAssignments = this.data?.hasInitialAssignments ?? true;
  public readonly hasGoBack = this.data?.hasGoBack ?? false;
  public readonly saveHandler$ = this.data?.saveHandler$;
  public offers = this.data?.offers!;
  public isLoading = this.data?.isLoading ?? false;

  @Output() public submitClick = new EventEmitter<{
    offerIds: number[];
    talentPoolIds: number[];
  }>();

  @ViewChild('defaultTpl', { static: true }) public default: TemplateRef<any>;
  @ViewChild('groupTpl', { static: true }) public group: TemplateRef<any>;

  public SortOptions: typeof SortOptions = SortOptions;
  public isSearchEnabled: boolean = OffersAssignmentsSelectModes.Reorder !== this.data?.selectMode;
  public isHeaderHidden: boolean = OffersAssignmentsSelectModes.Reorder === this.data?.selectMode;
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public sort$: BehaviorSubject<string> = new BehaviorSubject(SortOptions.Title);
  public isSelectedOfferWithAssignedHires = false;
  public DropdownWidth = DropdownWidth;
  public departmentTooltipEnabledMap: Record<number, boolean> = {};
  private newJobAssignments: number[] = [];
  private newTalentPoolAssignments: number[] = [];
  private ngOnDestroy$ = new Subject<void>();

  @ViewChildren('departmentRef') private set departmentRef(departmentRef: QueryList<ElementRef>) {
    if (departmentRef && departmentRef.length) {
      this.departmentTooltipEnabledMap = this.getIsTooltipVisibleMap(departmentRef.toArray());
      this.cdRef.detectChanges();
    }
  }

  constructor() {
    const cdRef = inject(ChangeDetectorRef);

    super(cdRef);
    this.cdRef = cdRef;

    this.createState({
      groupedOffersByKind: null,
      searchControlValue: '',
      newJobAssignments: [],
      newTalentPoolAssignments: [],
      activeSort: SortOptions.Title,
      countries: injectCountryMap(),
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.offers && changes.offers.currentValue) {
      this.groupOffers(changes.offers.currentValue);
    }

    if (changes.jobAssignments || changes.talentPoolAssignments) {
      this.newJobAssignments = this.initialJobAssignments || [...this.jobAssignments];
      this.newTalentPoolAssignments = this.initialTalentPoolAssignments || [
        ...this.talentPoolAssignments,
      ];
      this.setState({
        ...this.state,
        newJobAssignments: this.newJobAssignments,
        newTalentPoolAssignments: this.newTalentPoolAssignments,
      });
    }
  }

  public ngOnInit(): void {
    const search$ = this.searchControl.valueChanges;
    const destroy$ = this.ngOnDestroy$.asObservable();

    observableCombineLatest(
      this.sort$,
      search$.pipe(startWith(this.searchControl.value)),
      (sort, search) => ({
        sort,
        search,
      }),
    )
      .pipe(debounceTime(80), takeUntil(destroy$))
      .subscribe(_ => {
        this.groupOffers(this.data?.offers ?? []);
      });

    if (this.data) {
      // @ts-ignore
      objectKeys(this.data).forEach(k => (this[k] = this.data[k]));
      if (this.data.offers.length) {
        this.groupOffers(this.data.offers);
      }
    }
    this.newJobAssignments = this.initialJobAssignments || [...this.jobAssignments];
    this.newTalentPoolAssignments = this.initialTalentPoolAssignments || [
      ...this.talentPoolAssignments,
    ];

    this.searchControl.valueChanges
      .pipe(takeUntil(destroy$))
      .subscribe((searchControlValue: string) => {
        this.setState({ ...this.state, searchControlValue });
      });

    this.sort$.pipe(takeUntil(destroy$)).subscribe(activeSort => {
      this.setState({ ...this.state, activeSort });
    });

    this.checkIsSelectedOfferWithAssignedHires();
  }

  public ngAfterViewInit(): void {
    this.cdRef.detectChanges();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public get sortOptionsToArray(): SortOptions[] {
    return Object.values(this.SortOptions);
  }

  public get title(): string {
    return this.searchControl.value;
  }

  public get sort(): string {
    return this.sort$.getValue();
  }

  public get sortButtonLabel(): string {
    return this.getSortLabelTranslation(this.state.activeSort);
  }

  public getSortLabelTranslation(sortOption: string): string {
    return this.i18n.translate(`app.offers.sort_by.${sortOption}`);
  }

  public getJobStatusTranslation(offerStatus: string): string {
    return this.i18n.translate(`app.offers.status.${offerStatus}`);
  }

  public filterJobs(offers: OfferSelectDialogItem[], sort: boolean): TemplateGroup[] {
    const sortedOffers = sort ? this.sortAlphabetically(offers) : offers;
    const filteredByTitle = this.filterByTitle(sortedOffers);

    return this.groupOffersBy(filteredByTitle)!;
  }

  public changeSortOption(sortOption: SortOptionsType): void {
    this.sort$.next(sortOption);
  }

  public toggleAssignment(offer: OfferSelectDialogItem): void {
    if ([...this.newJobAssignments, ...this.newTalentPoolAssignments].includes(offer.id)) {
      this.newJobAssignments = this.newJobAssignments.filter(id => id !== offer.id);
      this.newTalentPoolAssignments = this.newTalentPoolAssignments.filter(id => id !== offer.id);
    } else {
      switch (this.selectMode) {
        case OffersAssignmentsSelectModes.Single:
          this.newJobAssignments = [...(offer.kind === OfferKind.Job ? [offer.id] : [])];
          this.newTalentPoolAssignments = [
            ...(offer.kind === OfferKind.TalentPool ? [offer.id] : []),
          ];
          break;
        case OffersAssignmentsSelectModes.Multi:
          this.newJobAssignments.push(...(offer.kind === OfferKind.Job ? [offer.id] : []));
          this.newTalentPoolAssignments.push(
            ...(offer.kind === OfferKind.TalentPool ? [offer.id] : []),
          );
          break;
        default:
          break;
      }
    }
    this.checkIsSelectedOfferWithAssignedHires();
    this.setState({
      ...this.state,
      newJobAssignments: { ...this.newJobAssignments },
      newTalentPoolAssignments: { ...this.newTalentPoolAssignments },
    });
  }

  public isEmpty(): boolean {
    return (
      this.offersGroupEmpty(this.state.groupedOffersByKind![0]) &&
      this.offersGroupEmpty(this.state.groupedOffersByKind![1])
    );
  }

  public isSelected(offer: OfferSelectDialogItem): boolean {
    return [...this.newJobAssignments, ...this.newTalentPoolAssignments].includes(offer.id);
  }

  public isSearching(): boolean {
    return !!this.searchControl.value;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public isDisabled(): boolean {
    return (
      !this.isAlwaysSaveable &&
      deepEqual(
        this.newJobAssignments.sort(),
        this.hasInitialAssignments ? this.jobAssignments.sort() : [],
      ) &&
      deepEqual(
        this.newTalentPoolAssignments.sort(),
        this.hasInitialAssignments ? this.talentPoolAssignments.sort() : [],
      )
    );
  }

  public cancel(): void {
    this.dialog.close();
  }

  public goBack() {
    this.dialog.close({ goBack: true });
  }

  public submit(): void {
    this.trackUnhire();
    if (this.saveHandler$) {
      this.isLoading = true;
      this.cdRef.detectChanges();
      this.saveHandler$(this.newJobAssignments, this.newTalentPoolAssignments)
        .pipe(take(1), takeUntil(this.ngOnDestroy$))
        .subscribe(result => {
          this.isLoading = false;
          this.cdRef.detectChanges();

          if (!result) {
            return;
          }

          this.dialog.close({
            offerIds: this.newJobAssignments,
            talentPoolIds: this.newTalentPoolAssignments,
          });
        });
    } else {
      this.submitClick.emit({
        offerIds: this.newJobAssignments,
        talentPoolIds: this.newTalentPoolAssignments,
      });
    }
  }

  public trackByGroupTitle(index: number, group: GroupByKind): string {
    return group.title;
  }

  public trackByOffer: TrackByFunction<OfferSelectDialogItem> = (
    index: number,
    offer: OfferSelectDialogItem,
  ): number => offer.id;

  public trackByTemplateGroup(index: number, templateGroup: TemplateGroup) {
    return templateGroup.key;
  }

  public trackByValue<V>(index: number, value: V): V {
    return value;
  }

  public getTemplate(templateKind: TemplateKind.Default | TemplateKind.Group): TemplateRef<any> {
    return this[templateKind || TemplateKind.Default];
  }

  public offersGroupEmpty(groupedOffersByKind: GroupByKind): boolean {
    const { groups } = groupedOffersByKind;
    return groups.reduce((acc, group) => acc + group.items.length, 0) === 0;
  }

  public dismissInfobox(): void {
    this.cdRef.detectChanges();
  }

  public onDrop(event: CdkDragDrop<any>): void {
    const offers = [...this.offers];
    moveItemInArray(offers, event.previousIndex, event.currentIndex);
    this.offers = offers;
    this.newJobAssignments = offers.map(offer => offer.id);
    this.groupOffers(this.offers);
    this.cdRef.detectChanges();
  }

  private filterTalentPools(offers: OfferSelectDialogItem[]): TemplateGroup[] {
    const sortedOffers = this.sortAlphabetically(offers);
    return [{ items: this.filterByTitle(sortedOffers), templateKind: TemplateKind.Default }];
  }

  private groupOffersBy(filteredOffers: OfferSelectDialogItem[]): TemplateGroup[] | undefined {
    switch (this.sort) {
      case SortOptions.Title:
        return [{ groupTitle: 'title', items: filteredOffers, templateKind: TemplateKind.Default }];
      case SortOptions.Department:
      case SortOptions.Country:
      case SortOptions.Location:
        const groups = this.convertGroups(filteredOffers, this.sort);
        return this.sortByItemsLength(groups);
      case SortOptions.Status:
        const statusGroups = this.convertGroups(filteredOffers, this.sort);
        return this.sortGroupsByStatus(statusGroups);
      default:
        return undefined;
    }
  }

  private checkIsSelectedOfferWithAssignedHires(): void {
    if (this.data?.offersWithAssignedHires) {
      this.isSelectedOfferWithAssignedHires = !!this.data.offersWithAssignedHires.find(
        offerWithAssignedHires =>
          this.newJobAssignments.find(assignment => assignment === offerWithAssignedHires.id),
      );
    }
  }

  private convertGroups(filteredOffers: OfferSelectDialogItem[], sort: string): TemplateGroup[] {
    const isCountry = sort === SortOptions.Country;
    const isLocation = sort === SortOptions.Location;
    const groups = isLocation
      ? this.getOffersGroupedByLocation(filteredOffers)
      : groupByKey(
          filteredOffers,
          isCountry ? 'countryCode' : <keyof OfferSelectDialogItemBase>sort,
        );

    return Object.keys(groups).map(key => {
      const title = isCountry
        ? this.state.countries[key]?.name || this.i18n.translate('app.offers.without_country')
        : key === 'null'
          ? this.i18n.translate('app.assignments.editor.without_department')
          : key;
      return {
        groupTitle: title,
        items: groups[key],
        templateKind: TemplateKind.Group,
        key: key,
      };
    });
  }

  private filterByTitle(offers: OfferSelectDialogItem[]): OfferSelectDialogItem[] {
    if (this.title && !this.title.length) {
      return offers;
    }
    return offers.filter(offer => includesWithTrim(offer.title, this.title));
  }

  private groupOffers(offers: OfferSelectDialogItem[]): void {
    const isReorderMode = OffersAssignmentsSelectModes.Reorder === this.selectMode;
    this.setState({
      ...this.state,
      groupedOffersByKind: [
        {
          title: this.i18n.translate('app.common.jobs'),
          groups: this.filterJobs(
            offers.filter(offer => offer.kind === 'job'),
            !isReorderMode,
          ),
          sortable: !isReorderMode,
        },
        {
          title: this.i18n.translate('app.common.talent_pools'),
          groups: this.filterTalentPools(offers.filter(offer => offer.kind === 'talent_pool')),
          sortable: false,
        },
      ],
    });
  }

  private getOffersGroupedByLocation(
    offers: OfferSelectDialogItem[],
  ): Record<string, OfferSelectDialogItem[]> {
    const locationOfferMap: Map<string, number[]> = new Map();
    const offersById: { [key: number]: OfferSelectDialogItem } = {};
    const offersWithLocations = offers.filter(o => o.locationIds!.length > 0);
    const offersWithoutLocations = offers.filter(o => o.locationIds!.length === 0);

    offersWithLocations.forEach(offer => {
      offersById[offer.id] = offer;
      (offer.locations || []).forEach(location => {
        if (locationOfferMap.has(location.name)) {
          locationOfferMap.set(location.name, [...locationOfferMap.get(location.name)!, offer.id]);
        } else {
          locationOfferMap.set(location.name, [offer.id]);
        }
      });
    });

    const locationGroupNames = Array.from(locationOfferMap.keys());
    const groupWithoutLocations = this.i18n.translate('app.offers.without_locations');
    const groupsByLocation: Record<string, OfferSelectDialogItem[]> = {
      ...locationGroupNames.reduce((acc: { [key: string]: OfferSelectDialogItem[] }, groupName) => {
        if (!acc[groupName]) {
          acc[groupName] = locationOfferMap.get(groupName)!.map(id => offersById[id]);
        }

        return acc;
      }, {}),
      [groupWithoutLocations]: offersWithoutLocations,
    };

    return groupsByLocation;
  }

  private sortGroupsByStatus(templateGroups: TemplateGroup[]): TemplateGroup[] {
    const pattern = [
      OfferStatus.Published,
      OfferStatus.Internal,
      OfferStatus.Draft,
      OfferStatus.Closed,
    ];
    let sortGroups: TemplateGroup[] = [];
    pattern.forEach(status => {
      templateGroups.forEach(group => {
        if (group.key === status) {
          sortGroups = [...sortGroups, group];
        }
      });
    });
    return sortGroups;
  }

  private sortAlphabetically(offers: OfferSelectDialogItem[]): OfferSelectDialogItem[] {
    return [...offers].sort((a, b) =>
      (a['title'] || '').toLowerCase().localeCompare((b['title'] || '').toLowerCase()),
    );
  }

  private sortByItemsLength(templateGroups: TemplateGroup[]): TemplateGroup[] {
    return templateGroups.sort((a, b) => b.items.length - a.items.length);
  }

  private trackUnhire(): void {
    if (this.data?.offersWithAssignedHires) {
      this.candidateAnalytics.trackUnhire(AnalyticsMode.Bulk, TrackUnhireTrigger.REMOVE_FROM_JOB);
    }
  }

  private getIsTooltipVisibleMap(rows: ElementRef[]): Record<number, boolean> {
    return rows.reduce(
      (acc, row) => {
        const offerId = Number(row.nativeElement.dataset.offerId);
        acc[offerId] = row.nativeElement.offsetWidth < row.nativeElement.scrollWidth;
        return acc;
      },
      <Record<number, boolean>>{},
    );
  }
}
